import { z } from "zod";
import { assetKeySchema } from "./primitives.js";

export const audioRoles = ["head-voice", "body-voice", "end-voice", "original", "music"] as const;

export const audioTrackSchema = z.object({
  id: z.string().min(1).max(64),
  role: z.enum(audioRoles),
  /**
   * Every track plays from a real file, including `original` — the recording's
   * audio is extracted to its own asset during ingest. Nothing reads sound out
   * of the BODY video, which is always muted, so one code path renders all of
   * it and any track can be swapped or silenced on its own.
   */
  assetKey: assetKeySchema.optional(),
  /**
   * A muted track is kept rather than deleted. Generating narration silences
   * the original audio, and a user who wants it back should not have to rebuild
   * the timeline to get it.
   */
  enabled: z.boolean().default(true),
  /** Seconds from the start of the finished video. */
  startAt: z.number().nonnegative().default(0),
  duration: z.number().gt(0),
  /** Seconds trimmed from the head of the source audio. */
  trimStart: z.number().nonnegative().default(0),
  volume: z.number().min(0).max(2).default(1),
  fadeIn: z.number().min(0).max(10).default(0),
  fadeOut: z.number().min(0).max(10).default(0),
  loop: z.boolean().default(false),
});

export type AudioTrack = z.infer<typeof audioTrackSchema>;
export type AudioRole = (typeof audioRoles)[number];

/**
 * The tracks that actually sound.
 *
 * The recording's own audio and the BODY narration are one or the other, never
 * both: two voices saying different things over the same footage is not a mix
 * anybody wants. The builder and the editor keep the flags exclusive, and this
 * enforces it at playback too, because timelines saved before that rule have
 * both switched on and would otherwise go on playing both until a rebuild.
 *
 * Only BODY narration. HEAD and END voices play over cards, where the
 * recording is not running and there is nothing for them to clash with.
 */
export function audibleTracks(tracks: AudioTrack[]): AudioTrack[] {
  const originalOn = tracks.some((track) => track.role === "original" && track.enabled);
  return tracks.filter((track) => track.enabled && !(originalOn && track.role === "body-voice"));
}
